import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../../../api/client";

const CATEGORIES = [
  "Salon",
  "Barbershop",
  "Beauty studio",
  "Nail studio",
  "Makeup studio",
  "Brow & lash studio",
  "Skin clinic",
  "Spa / wellness beauty studio",
  "Tattoo studio",
  "PMU studio",
  "Piercing studio",
  "Aesthetics clinic",
  "Multi-service beauty studio",
] as const;

const COMPLIANCE_CATEGORIES = new Set([
  "Tattoo studio",
  "PMU studio",
  "Piercing studio",
  "Aesthetics clinic",
]);

type ProfileDraft = {
  businessName: string;
  ownerName: string;
  companyEmail: string;
  phone: string;
  category: string;
  serviceAreas: string;
  branchCount: string;
  operatingHoursSummary: string;
  description: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  instagramUrl: string;
  websiteUrl: string;
  complianceRegistrationNumber: string;
  complianceAuthorityRegion: string;
  compliancePractitionerNote: string;
};

type ProviderDetailResponse = {
  queueItem?: {
    providerId?: string | null;
  };
  profile?: {
    businessName?: string | null;
    ownerName?: string | null;
    companyEmail?: string | null;
    phone?: string | null;
    category?: string | null;
    description?: string | null;
    serviceAreas?: string[] | null;
    branchCount?: number | null;
    operatingHoursSummary?: string | null;
  };
};

type SaveProfilePayload = {
  businessName: string;
  ownerName: string;
  companyEmail: string;
  phone: string;
  category: string;
  description: string;
  serviceAreas: string[];
  branchCount: number;
  operatingHoursSummary: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postcode: string;
  instagramUrl?: string;
  websiteUrl?: string;
  complianceRegistrationNumber?: string;
  complianceAuthorityRegion?: string;
  compliancePractitionerNote?: string;
};

const emptyDraft: ProfileDraft = {
  businessName: "",
  ownerName: "",
  companyEmail: "",
  phone: "",
  category: "",
  serviceAreas: "",
  branchCount: "1",
  operatingHoursSummary: "",
  description: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postcode: "",
  instagramUrl: "",
  websiteUrl: "",
  complianceRegistrationNumber: "",
  complianceAuthorityRegion: "",
  compliancePractitionerNote: "",
};

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "1rem",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const fieldStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.45rem",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.92rem",
  fontWeight: 600,
  color: "#334155",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.85rem 0.95rem",
  fontSize: "0.98rem",
  color: "#0f172a",
  background: "#ffffff",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 120,
  resize: "vertical",
};

const statusStyle = (
  tone: "success" | "error" | "neutral",
): React.CSSProperties => ({
  margin: 0,
  padding: "0.75rem 0.9rem",
  borderRadius: 12,
  fontSize: "0.9rem",
  lineHeight: 1.5,
  background:
    tone === "success"
      ? "#dcfce7"
      : tone === "error"
        ? "#fef2f2"
        : "#f3f4f6",
  color:
    tone === "success"
      ? "#166534"
      : tone === "error"
        ? "#b91c1c"
        : "#475569",
});

const buttonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 12,
  padding: "0.85rem 1.1rem",
  fontWeight: 700,
  cursor: "pointer",
};

function businessTypeToCategory(value?: string | null): string {
  switch (value) {
    case "BARBER":
      return "Barbershop";
    case "NAILSTUDIO":
      return "Nail studio";
    case "TATTOOSHOP":
      return "Tattoo studio";
    case "PIERCINGSTUDIO":
      return "Piercing studio";
    case "SALON":
      return "Salon";
    default:
      return value ?? "";
  }
}

function getComplianceLabels(category: string) {
  switch (category) {
    case "Tattoo studio":
      return {
        title: "Tattoo compliance details",
        registration: "Tattoo licence number",
        region: "Health authority region",
        note: "Sterilisation / hygiene note",
      };
    case "PMU studio":
      return {
        title: "Permanent makeup compliance details",
        registration: "PMU certification or licence number",
        region: "Regulatory / practice region",
        note: "Practitioner / equipment note",
      };
    case "Piercing studio":
      return {
        title: "Piercing compliance details",
        registration: "Piercing licence or registration number",
        region: "Authority / council region",
        note: "Hygiene / aftercare note",
      };
    case "Aesthetics clinic":
      return {
        title: "Aesthetics compliance details",
        registration: "Clinic or practitioner registration number",
        region: "Regulatory / operating region",
        note: "Practitioner qualification or scope note",
      };
    default:
      return null;
  }
}

export default function ProviderProfileStepPage() {
  const [providerId, setProviderId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProfileDraft>(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const detail = await apiFetch<ProviderDetailResponse>(
          "/provider/onboarding/me",
        );

        if (!active) {
          return;
        }

        const resolvedProviderId = detail.queueItem?.providerId;

        if (!resolvedProviderId) {
          throw new Error("No provider is linked to this account.");
        }

        const profile = detail.profile ?? {};

        setProviderId(resolvedProviderId);
        setDraft({
          businessName: profile.businessName ?? "",
          ownerName: profile.ownerName ?? "",
          companyEmail: profile.companyEmail ?? "",
          phone: profile.phone ?? "",
          category: businessTypeToCategory(profile.category),
          serviceAreas: profile.serviceAreas?.join(", ") ?? "",
          branchCount: String(profile.branchCount ?? 1),
          operatingHoursSummary: profile.operatingHoursSummary ?? "",
          description: profile.description ?? "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          postcode: "",
          instagramUrl: "",
          websiteUrl: "",
          complianceRegistrationNumber: "",
          complianceAuthorityRegion: "",
          compliancePractitionerNote: "",
        });
      } catch (loadError) {
        if (active) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Unable to load business profile.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadProfile();

    return () => {
      active = false;
    };
  }, []);

  const complianceLabels = getComplianceLabels(draft.category);
  const complianceSensitive = COMPLIANCE_CATEGORIES.has(draft.category);

  const completedFields = useMemo(() => {
    const values = [
      draft.businessName,
      draft.ownerName,
      draft.companyEmail,
      draft.phone,
      draft.category,
      draft.serviceAreas,
      draft.branchCount,
      draft.operatingHoursSummary,
      draft.description,
      draft.addressLine1,
      draft.city,
      draft.postcode,
    ];

    if (complianceSensitive) {
      values.push(
        draft.complianceRegistrationNumber,
        draft.complianceAuthorityRegion,
        draft.compliancePractitionerNote,
      );
    }

    return values.filter((value) => value.trim().length > 0).length;
  }, [complianceSensitive, draft]);

  const totalFields = complianceSensitive ? 15 : 12;
  const completionPercent = Math.round(
    (completedFields / totalFields) * 100,
  );

  const updateField =
    (field: keyof ProfileDraft) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setDraft((current) => ({
        ...current,
        [field]: event.target.value,
      }));

      setSavedAt(null);
      setError(null);
    };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!providerId) {
      setError("No provider is linked to this account.");
      return;
    }

    setSaving(true);
    setSavedAt(null);
    setError(null);

    const payload: SaveProfilePayload = {
      businessName: draft.businessName.trim(),
      ownerName: draft.ownerName.trim(),
      companyEmail: draft.companyEmail.trim(),
      phone: draft.phone.trim(),
      category: draft.category.trim(),
      description: draft.description.trim(),
      serviceAreas: draft.serviceAreas
        .split(",")
        .map((area) => area.trim())
        .filter(Boolean),
      branchCount: Number(draft.branchCount) || 1,
      operatingHoursSummary: draft.operatingHoursSummary.trim(),
      addressLine1: draft.addressLine1.trim(),
      addressLine2: draft.addressLine2.trim() || undefined,
      city: draft.city.trim(),
      postcode: draft.postcode.trim().toUpperCase(),
      instagramUrl: draft.instagramUrl.trim() || undefined,
      websiteUrl: draft.websiteUrl.trim() || undefined,
      complianceRegistrationNumber:
        draft.complianceRegistrationNumber.trim() || undefined,
      complianceAuthorityRegion:
        draft.complianceAuthorityRegion.trim() || undefined,
      compliancePractitionerNote:
        draft.compliancePractitionerNote.trim() || undefined,
    };

    try {
      await apiFetch<ProviderDetailResponse>(
        `/provider/onboarding/${providerId}/profile`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
      );

      setSavedAt(new Date().toLocaleTimeString());
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to save business profile.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={cardStyle}>
        <p style={statusStyle("neutral")}>
          Loading business profile...
        </p>
      </div>
    );
  }

  if (!providerId) {
    return (
      <div style={cardStyle}>
        <p style={statusStyle("error")}>
          {error ?? "No provider is linked to this account."}
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h2 style={{ margin: 0, color: "#0f172a", fontSize: "1.4rem" }}>
              Business profile
            </h2>

            <p
              style={{
                margin: "0.5rem 0 0",
                color: "#475569",
                lineHeight: 1.7,
              }}
            >
              Capture the business identity, provider category, and operating
              basics that staff will review before activation.
            </p>
          </div>

          <span
            style={{
              display: "inline-flex",
              padding: "0.35rem 0.7rem",
              borderRadius: 999,
              background:
                completionPercent >= 75 ? "#dcfce7" : "#eef2ff",
              color:
                completionPercent >= 75 ? "#166534" : "#3730a3",
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            {completionPercent}% complete
          </span>
        </div>
      </section>

      {error ? <p style={statusStyle("error")}>{error}</p> : null}

      {savedAt ? (
        <p style={statusStyle("success")}>
          Business profile saved at {savedAt}.
        </p>
      ) : null}

      <form onSubmit={handleSave} style={{ display: "grid", gap: "1rem" }}>
        <section style={cardStyle}>
          <h3 style={{ margin: 0, color: "#0f172a" }}>Identity</h3>

          <div style={{ ...gridStyle, marginTop: "1rem" }}>
            <Field label="Business name">
              <input
                style={inputStyle}
                value={draft.businessName}
                onChange={updateField("businessName")}
                required
              />
            </Field>

            <Field label="Owner name">
              <input
                style={inputStyle}
                value={draft.ownerName}
                onChange={updateField("ownerName")}
                required
              />
            </Field>

            <Field label="Company email">
              <input
                style={inputStyle}
                type="email"
                value={draft.companyEmail}
                onChange={updateField("companyEmail")}
                required
              />
            </Field>

            <Field label="Phone">
              <input
                style={inputStyle}
                type="tel"
                value={draft.phone}
                onChange={updateField("phone")}
                required
              />
            </Field>

            <Field label="Business category">
              <select
                style={inputStyle}
                value={draft.category}
                onChange={updateField("category")}
                required
              >
                <option value="">Select category</option>

                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Branch count">
              <input
                style={inputStyle}
                type="number"
                min={1}
                value={draft.branchCount}
                onChange={updateField("branchCount")}
                required
              />
            </Field>
          </div>
        </section>

        <section style={cardStyle}>
          <h3 style={{ margin: 0, color: "#0f172a" }}>
            Location and coverage
          </h3>

          <div style={{ ...gridStyle, marginTop: "1rem" }}>
            <Field label="Address line 1">
              <input
                style={inputStyle}
                value={draft.addressLine1}
                onChange={updateField("addressLine1")}
                required
              />
            </Field>

            <Field label="Address line 2">
              <input
                style={inputStyle}
                value={draft.addressLine2}
                onChange={updateField("addressLine2")}
              />
            </Field>

            <Field label="City">
              <input
                style={inputStyle}
                value={draft.city}
                onChange={updateField("city")}
                required
              />
            </Field>

            <Field label="Postcode">
              <input
                style={inputStyle}
                value={draft.postcode}
                onChange={updateField("postcode")}
                required
              />
            </Field>

            <Field label="Service areas">
              <input
                style={inputStyle}
                value={draft.serviceAreas}
                onChange={updateField("serviceAreas")}
                placeholder="Belfast, Lisburn"
              />
            </Field>
          </div>
        </section>

        <section style={cardStyle}>
          <h3 style={{ margin: 0, color: "#0f172a" }}>
            Business details
          </h3>

          <div style={{ ...gridStyle, marginTop: "1rem" }}>
            <Field label="Operating hours summary">
              <input
                style={inputStyle}
                value={draft.operatingHoursSummary}
                onChange={updateField("operatingHoursSummary")}
                placeholder="Mon–Sat, 09:00–18:00"
              />
            </Field>

            <Field label="Instagram URL">
              <input
                style={inputStyle}
                type="url"
                value={draft.instagramUrl}
                onChange={updateField("instagramUrl")}
              />
            </Field>

            <Field label="Website URL">
              <input
                style={inputStyle}
                type="url"
                value={draft.websiteUrl}
                onChange={updateField("websiteUrl")}
              />
            </Field>

            <Field label="Business description" fullWidth>
              <textarea
                style={textareaStyle}
                value={draft.description}
                onChange={updateField("description")}
              />
            </Field>
          </div>
        </section>

        {complianceLabels ? (
          <section
            style={{
              ...cardStyle,
              borderColor: "#fde68a",
              background: "#fffbeb",
            }}
          >
            <h3 style={{ margin: 0, color: "#0f172a" }}>
              {complianceLabels.title}
            </h3>

            <div style={{ ...gridStyle, marginTop: "1rem" }}>
              <Field label={complianceLabels.registration}>
                <input
                  style={inputStyle}
                  value={draft.complianceRegistrationNumber}
                  onChange={updateField(
                    "complianceRegistrationNumber",
                  )}
                />
              </Field>

              <Field label={complianceLabels.region}>
                <input
                  style={inputStyle}
                  value={draft.complianceAuthorityRegion}
                  onChange={updateField(
                    "complianceAuthorityRegion",
                  )}
                />
              </Field>

              <Field label={complianceLabels.note} fullWidth>
                <textarea
                  style={textareaStyle}
                  value={draft.compliancePractitionerNote}
                  onChange={updateField(
                    "compliancePractitionerNote",
                  )}
                />
              </Field>
            </div>
          </section>
        ) : null}

        <section style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <p style={statusStyle(savedAt ? "success" : "neutral")}>
              {savedAt
                ? "Draft saved."
                : "Draft status: Not saved yet."}
            </p>

            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...buttonStyle,
                  border: "none",
                  background: "#111827",
                  color: "#ffffff",
                  opacity: saving ? 0.65 : 1,
                }}
              >
                {saving ? "Saving..." : "Save draft"}
              </button>

              <Link
                to="/provider/onboarding/verification"
                style={{
                  ...buttonStyle,
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  color: "#111827",
                  textDecoration: "none",
                }}
              >
                Continue to verification
              </Link>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
  fullWidth = false,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div
      style={{
        ...fieldStyle,
        gridColumn: fullWidth ? "1 / -1" : undefined,
      }}
    >
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}